import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  AfterViewInit,
} from '@angular/core';
import { ButtonVariant } from '../../types/form.type';
import { FileUpload } from 'primeng/fileupload';
import { SnackBarService } from '@hospitality-bot/shared/material';

@Component({
  selector: 'hospitality-bot-upload-csv',
  templateUrl: './upload-csv.component.html',
  styleUrls: ['./upload-csv.component.scss'],
})
export class UploadCsvComponent implements OnInit, AfterViewInit {
  @Input() uploadStatus: boolean;

  @Input() label: string = null;
  @Input() pageType: string;
  @Input() severity: ButtonSeverity = 'primary';
  @Input() variant: ButtonVariant = 'contained';
  @Input() documentType: string;
  @Input() isDisable = false;
  @Input() chooseIcon: string = 'pi-arrow-down';
  @Input() uploadIcon: string = 'pi-arrow-down';
  @Input() maxFileSize: number = 3145728;
  @Input() fileType: string = '.csv, .xls, .xlsx';
  @Input() isAuto: boolean = true;
  @ViewChild('upload') upload: FileUpload;

  constructor(private snackbarService: SnackBarService) {}

  @Output()
  fileData = new EventEmitter();
  ngOnInit(): void {}

  ngAfterViewInit() {
    if (this.upload) {
      const originalValidate = this.upload.validate.bind(this.upload);
      this.upload.validate = (file: File) => {
        const isValid = originalValidate(file);
        if (!isValid) {
          if (this.upload.msgs && this.upload.msgs.length > 0) {
            const lastMsg = this.upload.msgs[this.upload.msgs.length - 1];
            this.snackbarService.openSnackBarAsText(
              lastMsg.detail || lastMsg.summary || 'Invalid file type or size',
              '',
              { panelClass: 'error' }
            );
            this.upload.msgs = [];
          }
        }
        return isValid;
      };
    }
  }

  onSelectFile(event) {
    if (event.files && event.files[0]) {
      const file = event.files[0];
      const fileSize = file.size;

      // Validate file type before proceeding
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!this.checkFileType(fileExtension)) {
        this.snackbarService.openSnackBarAsText(
          `Invalid file type. Allowed type: ${this.fileType}`,
          '',
          { panelClass: 'error' }
        );
        this.resetFileInput();
        return;
      }

      if (fileSize <= +this.maxFileSize) {
        const reader = new FileReader();
        reader.onload = (_event) => {
          const binaryString = reader.result as string; // This will contain the binary data

          // For CSV files
          const rows = binaryString.split('\n'); // Split the CSV content into rows
          const nonEmptyRows = rows.filter((row) => row.trim().length > 0); // Filter out empty rows
          const filteredContent = nonEmptyRows.join('\n'); // Join non-empty rows back into CSV content

          const docsType = this.extractDocumentType(file.name);

          // Emit the file data along with other parameters
          const data = {
            file: file,
            pageType: this.pageType,
            documentType: this.documentType ?? docsType,
            binaryData: filteredContent, // Include the filtered binary data in the emitted data
          };
          this.fileData.emit(data);
        };

        // Read the file as binary data
        reader.readAsText(file);
        this.resetFileInput();
      } else {
        // Handle case where file size exceeds the maximum allowed size
      }
    }
  }

  checkFileType(extension: string) {
    return this.fileType.includes(extension);
  }
  resetFileInput() {
    this.upload.clear();
  }

  extractDocumentType(url) {
    // Convert the URL to lowercase to make the comparison case-insensitive
    var lowerCaseUrl = url.toLowerCase();

    // Check if the URL contains keywords indicating an image
    if (
      lowerCaseUrl.includes('png') ||
      lowerCaseUrl.includes('jpeg') ||
      lowerCaseUrl.includes('jpg') ||
      lowerCaseUrl.includes('gif')
    ) {
      return 'IMAGE';
    }

    // Check if the URL contains keywords indicating a video
    if (
      lowerCaseUrl.includes('mp4') ||
      lowerCaseUrl.includes('avi') ||
      lowerCaseUrl.includes('mov') ||
      lowerCaseUrl.includes('wmv')
    ) {
      return 'VIDEO';
    }

    // Check if the URL contains keywords indicating an audio file
    if (
      lowerCaseUrl.includes('mp3') ||
      lowerCaseUrl.includes('wav') ||
      lowerCaseUrl.includes('ogg') ||
      lowerCaseUrl.includes('aac')
    ) {
      return 'AUDIO';
    }

    // If the URL doesn't match any known types, return "unknown"
    return 'DOCUMENT';
  }
}
export type ButtonSeverity = 'reset' | 'secondary' | 'primary';
